import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import AnimatedArrow from "./AnimatedArrow";
import Breadcrumbs from "./Breadcrumbs";
import { sectors } from "../data/sectors";
import type { Product, ProductCategory } from "../types";
import ProductGrid from "./ProductGrid";
import { buildProductGroups } from "../lib/productGroups";
import { useLanguage } from "../context/LanguageContext";

const SECTOR_CATEGORIES: Record<string, ProductCategory[]> = {
  "fine-dining-hotels": ["Thermal", "Refrigeration", "ServingLines", "Neutral"],
  "cafes-bars": ["CoffeeBar", "Warewashing", "ServingLines"],
  "dark-kitchens": ["Thermal", "Industrial", "Warewashing", "Neutral"],
};

export default function SectorPage() {
  const { t } = useLanguage();
  const { sectorId } = useParams<{ sectorId: string }>();
  const sector = sectors.find((s) => s.id === sectorId);
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    fetch("/api/products")
      .then((res) => res.json())
      .then(setProducts)
      .catch(() => {});
  }, []);

  if (!sector) {
    return <Navigate to="/" replace />;
  }

  const relevantCategories = SECTOR_CATEGORIES[sector.id] ?? [];
  const relatedProducts = products.filter((product) =>
    relevantCategories.includes(product.category)
  );
  const relatedGroups = buildProductGroups(relatedProducts);

  return (
    <>
      <div className="pt-24 md:pt-32">
        <Breadcrumbs items={[{ label: t("breadcrumbSectors") }, { label: sector.title }]} />
      </div>
      <section className="relative flex h-[60vh] min-h-[420px] w-full items-end overflow-hidden bg-ink">
        <img
          src={sector.image}
          alt={sector.title}
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-ink/40" />
        <div className="container-industrial relative z-10 pb-16 pt-40">
          <p className="eyebrow mb-4 hidden text-white/70 sm:block">{t("sectorEyebrow")}</p>
          <h1 className="font-display uppercase text-[2.5rem] font-semibold tracking-[0.3px] leading-[1.05] text-white md:text-5xl">
            {sector.title}
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70">
            {sector.description}
          </p>
          <a href="/contacts" className="btn-primary mt-9 inline-flex">
            {t("heroCtaSecondary")}
            <AnimatedArrow />
          </a>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32">
        <div className="container-industrial">
          <h2 className="font-display uppercase text-3xl font-semibold tracking-[0.3px] text-ink md:text-[2.5rem]">
            {t("sectorEquipmentHeading")}
          </h2>

          <ProductGrid groups={relatedGroups} columns="1-2-3" className="mt-14" />
        </div>
      </section>
    </>
  );
}
